import React, { useCallback, useEffect, useRef } from 'react';

import { FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';
import Validate from 'validate.js';

import { KEYS } from '../../../../../../constants/Common';

import Url from '../../../../../../services/Url';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../hooks/connectAuth';

import { FadeIn } from '../../../Common/Animations/Keyframes';
// @ts-ignore
import Checkbox from '../../../Common/Form/CheckBox';
import Input from '../../../Common/Form/Input/Input';
import { IAuthState } from '../../Store/Reducers';
import { IInputData, IKeyDown, Types } from '../../Store/SignUpReducer';
import InputEmail from './PersonalData/InputEmail';
import InputGender from './PersonalData/InputGender';
import InputPassword from './PersonalData/InputPassword/InputPassword';


interface IPersonalData {
    reset: boolean,
    name: IInputData,
    keyDown: IKeyDown,
    consentTerms: boolean,
    emailSubscription: boolean,
    customNamePlaceholder?: string,
}

type TPersonalData = IConnectedProps<IPersonalData>;

const Content = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    animation: ${FadeIn} .3s ease-out;
    gap: ${({ theme }) => theme.space.cat};

    & > u {
        cursor: pointer;
        font-size: 0.8125rem;
        margin-top: 0.8125rem;
        pointer-events: inherit;
        text-decoration: underline;
        transition: color .3s ease-out;
        color: ${({ theme }) => theme.colors.blue100};
    }
    
    & > p {
        width: 100%;
        font-size: 0.75em;
        text-align: center;
        line-height: normal;
        padding: 0 2.0938rem;
        margin: 0.8125em 0 0.9375rem;
        color: ${({ theme }) => theme.colors.blue200};
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.tablet.min}) {
        & > u {
            font-size: 1rem;
        }

        & > p {
            font-size: 0.9375rem;
            padding: 0 1.0938rem;
        }
    }
`;

const PersonalData: React.FC<TPersonalData> = ({ intl, dispatch, ...props }) => {
    const inputName = useRef<HTMLInputElement>(null);

    const emailConsentiment = intl.formatMessage({ id: 'auth.emailConsentiment' });
    const termsLink = Url.base((intl.formatMessage({ id: 'auth.help.termsLink' })));
    const privacyLink = Url.base(intl.formatMessage({ id: 'auth.help.privacyLink' }));

    const {
        name,
        reset,
        keyDown,
        consentTerms,
        emailSubscription,
        customNamePlaceholder
    } = props;

    const namePlaceholder = customNamePlaceholder || intl.formatMessage({ id: 'authFunctional.personalData.placeholders.name' });

    const handleChangeEmailSubscription = useCallback(() => dispatch({
        type: Types.CHANGE_PERSONAL_DATA,
        data: { emailSubscription: !emailSubscription }
    }), [dispatch, emailSubscription]);

    const handleChangeConsentSubscription = useCallback(() => dispatch({
        type: Types.CHANGE_PERSONAL_DATA,
        data: { consentTerms: !consentTerms }
    }), [dispatch, consentTerms]);

    const handleChangeName = ({ target }: React.ChangeEvent<HTMLInputElement>) => dispatch({
        type: Types.CHANGE_PERSONAL_FIELD_DATA,
        field: 'name',
        data: {
            value: target.value,
            error: ''
        }
    });

    const validateField = (field: string) => {
        const fieldRules = {
            presence: {
                message: intl.formatMessage({ id: 'authFunctional.errors.nameEmpty' })
            },
            length: {
                minimum: 3,
                tooShort: intl.formatMessage({ id: 'authFunctional.errors.nameLength' })
            },
            format: {
                pattern: '[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ ]+',
                message: intl.formatMessage({ id: 'authFunctional.errors.nameFormat' })
            }
        };

        const isValid = Validate({ name: name.value }, { name: fieldRules });

        const data = (!!isValid && name.touched)
            ? { error: isValid[field][0] }
            : { value: name.value };

        const action = {
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field,
            data
        };

        dispatch(action);
    };

    const handleBlurName = () => validateField('name');

    useEffect(() => {
        if (!reset) return;

        dispatch({ type: Types.CHANGE_PERSONAL_DATA_RESET });
    }, [reset, dispatch]);

    useEffect(() => {
        if (keyDown.key !== KEYS.ENTER) return;

        const { id } = (document.activeElement?.parentNode as HTMLDivElement);

        if (id === 'auth-email-subscription') {
            handleChangeEmailSubscription();
            return;
        }

        if (id === 'auth-consent-subscription') {
            handleChangeConsentSubscription();
        }
    }, [keyDown, handleChangeConsentSubscription, handleChangeEmailSubscription]);

    useEffect(() => {
        if (!inputName.current) return;

        inputName.current.focus();
    }, []);

    return (
        <Content data-cy="auth-personal-data-container">
            <Input
                order={4}
                id="auth-name"
                ref={inputName}
                value={name.value}
                error={name.error}
                onBlur={handleBlurName}
                onChange={handleChangeName}
                placeholder={namePlaceholder}
            />

            <InputGender />

            <InputEmail />

            <InputPassword />

            <Checkbox
                tabIndex={0}
                checked={emailSubscription}
                onCheckedChange={handleChangeEmailSubscription}
            >
                <span>{emailConsentiment}</span>
            </Checkbox>

            <Checkbox
                tabIndex={0}
                checked={consentTerms}
                onCheckedChange={handleChangeConsentSubscription}
            >
                <FormattedHTMLMessage
                    id="auth.termsConsentiment"
                    values={{ termsLink, privacyLink }}
                />
            </Checkbox>
        </Content>
    );
};

const mapStateToProps: IAuthMapStateToProps<IPersonalData> = (state, isComparison = false) => ((!isComparison)
    ? {
        reset: (state as IAuthState).Container.reset,
        name: (state as IAuthState).SignUp.personalData.name,
        consentTerms: (state as IAuthState).SignUp.personalData.consentTerms,
        emailSubscription: (state as IAuthState).SignUp.personalData.emailSubscription,
        customNamePlaceholder: (state as IAuthState).Container.communication.placeholderName,
        keyDown: (KEYS.ENTER === (state as IAuthState).SignUp.keyDown.key) ? (state as IAuthState).SignUp.keyDown : ''
    } :
    {
        name: state.name,
        reset: state.reset,
        keyDown: state.keyDown,
        consentTerms: state.consentTerms,
        emailSubscription: state.emailSubscription,
        customNamePlaceholder: state.customNamePlaceholder
    }
);

export default connectAuth(mapStateToProps)(PersonalData);
